import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {GROUP_KEYS, useGroupListQueryParams} from "@/api/groupApi";
import {useSelectedCompany} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import i18n from "@/i18n";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import useCampaignManagementStore from "@/store/campaignManagement";
import {COUNTRY_FILTERS} from "@/store/constants";
import {create} from "@/store/createStore";
import {createCampaignForCompanyAtom} from "@/store/jotaiAtoms";
import type {TCampaign, TCampaignStatus, TCampaignType, TCountry, TUgcContent} from "@/store/types";
import useUserStore from "@/store/user";
import urlify from "@/utils/urlify";
import type {AxiosError, AxiosResponse} from "axios";
import dayjs from "dayjs";
import {getDefaultStore} from "jotai";
import isEqual from "lodash/isEqual";
import sumBy from "lodash/sumBy";
import {toast} from "react-toastify";
import {z} from "zod";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";
import {useShallow} from "zustand/shallow";

export const MIN_CASTING_DAYS = 3;
export const MAX_CASTING_DAYS = 14;
export const MIN_PREPARATION_DAYS = 5;
export const MIN_PREPARATION_DAYS_CASH = 0;
export const MAX_PREPARATION_DAYS = 14;
export const MIN_ACTIVE_DAYS = 7;
export const MAX_ACTIVE_DAYS = 30;
export const ACCOUNT_TAG_LIMIT = 20;
export const HASHTAG_LIMIT = 30;

export const EVENT_ACTIVE_DAYS = 3;
export const EVENT_PREPARATION_DAYS = 3;
const ACTIVE_DAYS = 14;
const ACTIVE_DAYS_UGC = 7;
const PREPARATION_DAYS = 7;
const CASTING_DAYS = 10;

export const UGC_COST_PER_VIDEO_15_SECONDS = 79;
export const UGC_COST_PER_VIDEO_30_SECONDS = 99;
const UGC_CASH_REWARD_PERCENTAGE = 0.55;

export const BONUS_POOL_MIN_CASH_REWARD = 40;
export const BONUS_POOL_MIN_REQUIRED_APPLICANTS = 3;
export const BONUS_POOL_PERCENTAGE = 0.2;

export const MARKDOWN_SYMBOLS_PATTERN = /[*_\-#~>\[\]]/g;

const parseAsNumber = (param: number | null) => {
  if (param === null) return -1;
  const parsed = Number(param);
  if (isNaN(parsed)) return -1;
  return parsed;
};

const getChangedTags = (prefix: string, newTag: string) => {
  const formattedValue = newTag
    .replaceAll(`${prefix} `, "")
    .replace(new RegExp(`${prefix}+`, "g"), prefix)
    .replace(new RegExp(`${prefix}(\\w+)(?=${prefix})`, "g"), `${prefix}$1 `)
    .replace(new RegExp(`${prefix}\\s${prefix}$`, "g"), prefix);

  return formattedValue.trim().split(/[\s,]+/);
};

const CampaignMiscSchema = z.object({
  brandDoes: z
    .string()
    .min(1, "product_description_required")
    .refine(value => value.replace(MARKDOWN_SYMBOLS_PATTERN, "").length <= 750, {
      message: "product_description_max_length_error",
    }),
  title: z.string().min(1, "campaign_title_required"),
  sellingPrice: z.number().nullable().transform(parseAsNumber),
  cashReward: z.number().nullable().transform(parseAsNumber),
  longDescription: z.string().refine(value => value.replace(MARKDOWN_SYMBOLS_PATTERN, "").length <= 750, {
    message: "campaign_description_max_length_error",
  }),
  locations: z.array(z.any()).min(1, "countries_min_length_error"),
  castingDays: z.number().min(3, "casting_phase_min_length_error").max(14, "casting_phase_max_length_error"),
  picture: z.preprocess(value => value ?? "", z.string().min(1, "campaign_image_required")),
});

const CampaignInstagramSchema = z.object({
  task: z
    .string()
    .min(1, "task_required")
    .refine(value => value.replace(MARKDOWN_SYMBOLS_PATTERN, "").length <= 1200, {message: "task_max_length_error"}),
  accountTags: z
    .array(z.any())
    .min(1, "account_tag_required")
    .max(ACCOUNT_TAG_LIMIT, "account_tag_limit_reached")
    .refine(arr => arr.some(tag => tag.trim() !== "" && tag.trim() !== "@"), {message: "account_tag_required"}),
  hashtags: z.array(z.any()).max(HASHTAG_LIMIT, "hashtag_limit_reached"),
});

const CampaignTiktokSchema = z.object({
  task: z
    .string()
    .min(1, "task_required")
    .refine(value => value.replace(MARKDOWN_SYMBOLS_PATTERN, "").length <= 1200, {message: "task_max_length_error"}),
  accountTags: z.array(z.any()).max(ACCOUNT_TAG_LIMIT, "account_tag_limit_reached"),
  hashtags: z.array(z.any()).max(HASHTAG_LIMIT, "hashtag_limit_reached"),
  tiktokCampaignCode: z.string(),
});

const CampaignUgcSchema = z.object({
  ugcDontsDescription: z.string().refine(value => value.replace(MARKDOWN_SYMBOLS_PATTERN, "").length <= 1200, {
    message: "ugc_donts_max_length_error",
  }),
});

type TFileUploadResponse = {
  id: number;
  image: string;
  thumbnail?: string | null;
};

type TState = {
  campaign: TComposedCampaign;
  formState: TFormState;
  actions: TActions;
};

export type TContentRequirements = {
  stories: number;
  posts: number;
  reels: number;
} | null;

export type TComposedCampaign = {
  misc: {
    campaignType: TCampaignType;
    isOngoing: boolean;
    brandName: string;
    isServiceClothing: boolean;
    longDescription: string;
    castingStartDate: string;
    castingEndDate: string; //same as 'deadline'
    preparationEndDate: string;
    endDate: string; //same as 'completed'
    status: TCampaignStatus;
    title: string;
    locations: TCountry[];
    picture: string | null;
    images: {image: string; id: number}[];
    cashReward: number | null;
    id?: number;
    isRefluencedTagRequired: boolean;
    productVariant: string;
    sellingPrice: number | null;
    brandDoes: string;
    mainImageUploadProgress?: number | null;
    imageUploadProgress?: number | null;
    event?: boolean;
    eventAttendedByInfluencers?: boolean;
    eventAddressPlaceId?: string | null;
    eventAddressDescription?: string;
    eventAddressStreet?: string | null;
    eventAddressPostal?: string | null;
    eventAddressCity?: string | null;
    eventAddressCountry?: string | null;
    eventDuration: "ONE_DAY" | "MULTIPLE_DAYS";
    eventStartDate?: string | null;
    eventEndDate?: string | null;
    isBonusPoolActive?: boolean;
    castingDays: number | null;
    preparationDays: number | null;
    activeDays: number | null;
    companyId?: number;
    companyImage?: string | null;
    canCreateEvents?: boolean;
    skipCharacterLimits?: boolean;
    hasEditedPrice?: boolean;
    hasChosenContentOption?: boolean;
    isProduct: boolean;
    isVoucher: boolean;
    isCash: boolean;
    isExtended: boolean;
    isProInfluencer: boolean;
    digitalShopUrl?: string | null;
  };
  instagram: {
    isLinkStickerEnabled: boolean;
    linkSticker: string | null;
    isLinkStickerTitleEnabled: boolean;
    linkStickerTitle?: string | null;
    requiredContentUploads: TContentRequirements;
    task: string;
    hashtags: string[];
    accountTags: string[];
    contentOptionOne?: TContentRequirements | null;
    contentOptionTwo?: TContentRequirements | null;
    instagramPartnershipAds: boolean;
  };
  tiktok: {
    tiktokCampaignCode: string;
    hashtags: string[];
    accountTags: string[];
    task: string;
    numberOfMedias: number;
    tiktokSparkAds: boolean;
  };
  ugc: {
    isWithSubtitles: boolean;
    isSourceMaterialRequested: boolean;
    contentLanguage: "Swiss German" | "High German" | "English" | "French" | "Italian";
    ugcDontsDescription: string;
    updatedUgcContents: TUgcContent[];
    ugcContents: TUgcContent[];
    initialCashReward: number | null; // Based only on the selected UGC videos (excluding extra cash reward)
    extraCashReward: number | null;
  };
};

type TFormState = {
  step: 1 | 2 | 3;
  isEditable: boolean;
  errors: (string | [string, Record<string, unknown>])[];
};

// Define the specific type for the misc payload
type TMiscPayload = {
  number_of_feedposts: number;
  number_of_stories: number;
  number_of_reels: number;
  tiktok_number_of_medias: number;
  number_of_ugc: number;
  title: string;
  on_going: boolean;
  brand_name: string;
  long_description: string;
  brand_does: string;
  product_variant: string;
  selling_price: number;
  locations: string;
  casting_start: string;
  deadline: string;
  preparation: string;
  completed: string;
  preparation_days: number;
  commercial_days: number;
  cash_reward?: number; // Optional because it's conditional
  service_is_clothing: boolean;
  refluenced_tag_required: boolean;
  event?: boolean;
  event_attended_by_influencer?: boolean;
  event_address_place_id?: string | null;
  event_address_description?: string;
  event_address_street?: string | null;
  event_address_postal?: string | null;
  event_address_city?: string | null;
  event_address_country?: string | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
  bonus_pool?: boolean;
  casting_days: number | null;
  service_is_physical_product: boolean;
  service_is_voucher: boolean;
  service_is_cash: boolean;
  digital_shop_url?: string | null;
};

// Define specific payload types
type TInstagramPayload = {
  number_of_feedposts: number;
  number_of_stories: number;
  number_of_reels: number;
  hashtags: string;
  account_tags: string;
  task: string;
  link_sticker: string | null;
  instagram_branded_content_ads: boolean;
};

type TTiktokPayload = {
  tiktok: true;
  task: string;
  hashtags: string;
  account_tags: string;
  tiktok_campaign_code: string;
  tiktok_number_of_medias: number;
  tiktok_spark_ads: boolean;
};

type TUgcPayload = {
  ugc: true;
  ugc_dont_description: string;
  subtitles: boolean;
  source_material: boolean;
  number_of_ugc: number;
  content_language: string;
  ugc_extra_cash_reward: number;
};

type TActions = {
  misc: {
    setMiscValue<K extends keyof NonNullable<TComposedCampaign["misc"]>>(
      key: K,
      value: NonNullable<TComposedCampaign["misc"]>[K],
    ): void;
    setFormState<K extends keyof TFormState>(key: K, value: TFormState[K]): void;
    changeCampaignDate(
      key: "castingStartDate" | "castingEndDate" | "preparationEndDate" | "endDate",
      value: string,
    ): void;
    changePhaseLength(key: "castingPhase" | "preparationPhase" | "activePhase", value: number): void;
    saveCampaign(isDraft?: boolean): Promise<AxiosResponse<{id: number}>>;
    initWithExistingCampaign(campaign: TCampaign): void;
    initNewCampaign({campaignType}: {campaignType: TComposedCampaign["misc"]["campaignType"]}): Promise<void>;
    addMainCampaignImage(image: File | null): Promise<void>;
    addCampaignImages(images: FileList): Promise<void>;
    deleteImage(id: number): Promise<void>;
    resetStore(): void;
    buildPayload(): TMiscPayload;
    validate(): boolean;
    updateScheduling(): void;
    resetEventAddress(): void;
  };
  tiktok: {
    setTiktokValue<K extends keyof NonNullable<TComposedCampaign["tiktok"]>>(
      key: K,
      value: NonNullable<TComposedCampaign["tiktok"]>[K],
    ): void;
    changeHashTag(newValue: string): void;
    buildPayload(): TTiktokPayload;
    changeAccountTag(newValue: string): void;
    validate: () => boolean;
  };
  instagram: {
    setInstagramValue<K extends keyof NonNullable<TComposedCampaign["instagram"]>>(
      key: K,
      value: NonNullable<TComposedCampaign["instagram"]>[K],
    ): void;
    changeHashTag(newValue: string): void;
    changeAccountTag(newValue: string): void;
    buildPayload(): TInstagramPayload;
    validate: () => boolean;
  };
  ugc: {
    initWithExistingUgc(campaign: TCampaign): Record<keyof TComposedCampaign["ugc"], any>;
    setUgcValue<K extends keyof NonNullable<TComposedCampaign["ugc"]>>(
      key: K,
      value: NonNullable<TComposedCampaign["ugc"]>[K],
    ): void;
    addUgcVideo(campaignId?: number): Promise<void>;
    deleteUgcVideo(index: number): Promise<void>;
    addUgcScene(index: number): void;
    deleteUgcScene(videoIndex: number, sceneIndex: number): void;
    changeUgcScene(videoIndex: number, sceneIndex: number, key: "content" | "name", value: string): void;
    changeUgcVideo<K extends keyof TComposedCampaign["ugc"]["updatedUgcContents"][number]>(
      index: number,
      key: K,
      value: TComposedCampaign["ugc"]["updatedUgcContents"][number][K],
    ): void;
    buildPayload(): TUgcPayload;
    saveUgcChanges(): Promise<void>;
    calculateInitialCashReward(): void;
    validate: () => boolean;
  };
};

const initialState = {
  formState: {
    step: 1,
    isEditable: true,
    errors: [],
  },
  campaign: {
    misc: {
      campaignType: "instagram",
      isOngoing: false,
      brandName: "",
      isServiceClothing: false,
      longDescription: "",
      castingStartDate: "",
      castingEndDate: "",
      preparationEndDate: "",
      endDate: "",
      isRefluencedTagRequired: false,
      status: "draft",
      id: undefined,
      productVariant: "",
      title: "",
      locations: [],
      picture: null,
      images: [],
      sellingPrice: null,
      brandDoes: "",
      cashReward: null,
      mainImageUploadProgress: null,
      imageUploadProgress: null,
      eventDuration: "ONE_DAY",
      castingDays: null,
      preparationDays: null,
      activeDays: null,
      hasEditedPrice: false,
      hasChosenContentOption: true,
      isProduct: false,
      isVoucher: false,
      isCash: false,
      isExtended: false,
      isProInfluencer: false,
    },
    instagram: {
      task: "",
      isLinkStickerEnabled: true,
      linkSticker: "",
      isLinkStickerTitleEnabled: false,
      linkStickerTitle: null,
      requiredContentUploads: null,
      hashtags: [],
      accountTags: [],
      contentOptionOne: null,
      contentOptionTwo: null,
      instagramPartnershipAds: false,
    },
    tiktok: {
      tiktokCampaignCode: "",
      hashtags: [],
      accountTags: [],
      numberOfMedias: 1,
      task: "",
      tiktokSparkAds: true,
    },
    ugc: {
      isWithSubtitles: false,
      isSourceMaterialRequested: false,
      contentLanguage: "Swiss German",
      ugcDontsDescription: "",
      updatedUgcContents: [],
      ugcContents: [],
      initialCashReward: null,
      extraCashReward: null,
    },
  },
} as Omit<TState, "actions">;

export const useCampaignFormStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        actions: {
          misc: {
            buildPayload: () => {
              const campaign = get().campaign.misc;
              return {
                //the following settings are only here to provide a non-null amount of content for each required media type
                //the campaignType-specific buildPayload methods will override the values if necessary
                number_of_feedposts: 0,
                number_of_stories: 0,
                number_of_reels: 0,
                tiktok_number_of_medias: 0,
                number_of_ugc: 0,
                title: campaign.title,
                on_going: campaign.isOngoing,
                brand_name: campaign.brandName,
                long_description: campaign.longDescription,
                brand_does: campaign.brandDoes,
                product_variant: campaign.isProduct ? campaign.productVariant : "",
                selling_price: campaign.sellingPrice ?? 0,
                locations: campaign.locations.join(","),
                casting_start: dayjs(campaign.castingStartDate).format(),
                deadline: dayjs(campaign.castingEndDate).format(),
                preparation: dayjs(campaign.preparationEndDate).format(),
                completed: dayjs(campaign.endDate).format(),
                preparation_days: dayjs(campaign.preparationEndDate).diff(dayjs(campaign.castingEndDate), "days"),
                commercial_days: dayjs(campaign.endDate).diff(dayjs(campaign.preparationEndDate), "days"),
                cash_reward: campaign.campaignType === "ugc" ? undefined : (campaign.cashReward ?? 0),
                service_is_clothing: campaign.isServiceClothing,
                refluenced_tag_required: campaign.isRefluencedTagRequired,
                event: campaign.event,
                event_attended_by_influencer: campaign.eventAttendedByInfluencers,
                event_address_place_id: campaign.eventAddressPlaceId,
                event_address_description: campaign.eventAddressDescription,
                event_address_street: campaign.eventAddressStreet,
                event_address_postal: campaign.eventAddressPostal,
                event_address_city: campaign.eventAddressCity,
                event_address_country: campaign.eventAddressCountry,
                event_start_date: campaign.eventStartDate,
                event_end_date: campaign.eventEndDate,
                bonus_pool: campaign.isBonusPoolActive,
                casting_days: campaign.castingDays,
                service_is_physical_product: campaign.isProduct,
                service_is_voucher: campaign.isVoucher,
                service_is_cash: campaign.isCash,
                digital_shop_url: campaign.digitalShopUrl,
                pro_influencer: campaign.isProInfluencer,
              };
            },
            addMainCampaignImage: async image => {
              try {
                const campaign = get().campaign;
                const response = await client.patch(
                  CAMPAIGN_KEYS.mCampaign(campaign.misc.id!)[0],
                  {
                    picture: image,
                    picture1200: image,
                    picture640: image,
                    picture_vertial1080: image,
                    picture_vertial576: image,
                  },
                  {
                    headers: {
                      "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: progressEvent => {
                      if (!progressEvent.total) return;

                      const progress = (progressEvent.loaded / progressEvent.total) * 100;
                      set(state => {
                        state.campaign.misc.mainImageUploadProgress = Math.floor(progress);
                      });
                    },
                  },
                );

                set(state => {
                  state.campaign.misc.picture = response.data.picture;
                  state.campaign.misc.mainImageUploadProgress = null;
                });
              } catch (error) {
                const err = (error as AxiosError<{picture: string[]}>).response?.data?.picture?.[0];
                toast.error(`${i18n.t("error")} ${!!err ? " - " + err : ""}`);
                setTimeout(() => {
                  set(state => {
                    state.campaign.misc.mainImageUploadProgress = null;
                  });
                }, 1500);
              }
            },
            addCampaignImages: async images => {
              const uploadPromises: Promise<AxiosResponse<TFileUploadResponse>>[] = [];
              const currentImages = get().campaign.misc.images;

              for (let i = 0; i < images.length; i++) {
                if (5 - currentImages.length <= i) break;

                const image = images[i];
                const uploadPromise = client.post<TFileUploadResponse>(
                  `/campaign/${get().campaign.misc.id}/upload_image/`,
                  {image: image, thumbnail: image},
                  {
                    headers: {
                      "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: progressEvent => {
                      if (!progressEvent.total) return;

                      const progress = (progressEvent.loaded / progressEvent.total) * 100;
                      set(state => {
                        state.campaign.misc.imageUploadProgress = Math.floor(progress);
                      });
                    },
                  },
                );
                uploadPromises.push(uploadPromise);
              }

              try {
                const responses = await Promise.all(uploadPromises);

                set(state => {
                  state.campaign.misc.images = [...state.campaign.misc.images, ...responses.map(resp => resp.data)];
                  state.campaign.misc.imageUploadProgress = null;
                });
              } catch (error) {
                const err = (error as AxiosError<{image: string[]}>).response?.data?.image?.[0];
                toast.error(`${i18n.t("error")} ${!!err ? " - " + err : ""}`);
                setTimeout(() => {
                  set(state => {
                    state.campaign.misc.imageUploadProgress = null;
                  });
                }, 1500);
              }
            },
            deleteImage: async id => {
              const campaign = get().campaign;
              const pictureInUploadedImages = campaign.misc.images.find(image => image.id === id);

              if (!pictureInUploadedImages) return;

              await client.post(CAMPAIGN_KEYS.mCampaignImageDeletion(campaign.misc.id!)[0], {
                id: pictureInUploadedImages.id,
              });
              set(
                state => {
                  state.campaign.misc.images = state.campaign.misc.images.filter(image => image.id !== id);
                },
                false,
                "campaignFormStore/deleteImage",
              );
            },
            setMiscValue: (key, value) =>
              set(
                state => {
                  if (key === "cashReward" && typeof value === "number") {
                    const oldCashReward = get().campaign.misc.cashReward ?? 0;
                    const newCashReward = value!;
                    const isTikTok = get().campaign.misc.campaignType === "tiktok";
                    const isInstaWithReelOrPost =
                      get().campaign.misc.campaignType === "instagram" &&
                      (get().campaign.instagram.requiredContentUploads?.reels! > 0 ||
                        get().campaign.instagram.requiredContentUploads?.posts! > 0);

                    if (
                      oldCashReward < BONUS_POOL_MIN_CASH_REWARD &&
                      newCashReward >= BONUS_POOL_MIN_CASH_REWARD &&
                      (isTikTok || isInstaWithReelOrPost)
                    ) {
                      state.campaign.misc.isBonusPoolActive = true;
                    }

                    if (oldCashReward >= BONUS_POOL_MIN_CASH_REWARD && newCashReward < BONUS_POOL_MIN_CASH_REWARD) {
                      state.campaign.misc.isBonusPoolActive = false;
                    }
                  }

                  state.campaign.misc[key] = value;
                },
                false,
                "campaignFormStore/setMiscValue",
              ),
            setFormState: (key, value) =>
              set(
                state => {
                  state.formState[key] = value;
                },
                false,
                "campaignFormStore/setFormState",
              ),
            changeCampaignDate: (key, value) => {
              return set(
                state => {
                  const previousValue = dayjs(state.campaign.misc[key]);
                  const diff = dayjs(value).diff(previousValue);
                  if (key === "castingStartDate") {
                    state.campaign.misc.castingStartDate = value;

                    if (state.campaign.misc.event) return;

                    state.campaign.misc.castingEndDate = dayjs(state.campaign.misc.castingEndDate)
                      .add(diff)
                      .toISOString();
                    state.campaign.misc.preparationEndDate = dayjs(state.campaign.misc.preparationEndDate)
                      .add(diff)
                      .toISOString();
                    state.campaign.misc.endDate = dayjs(state.campaign.misc.endDate).add(diff).toISOString();
                  } else if (key === "castingEndDate") {
                    state.campaign.misc.castingEndDate = value;
                    state.campaign.misc.preparationEndDate = dayjs(state.campaign.misc.preparationEndDate)
                      .add(diff)
                      .toISOString();
                    state.campaign.misc.endDate = dayjs(state.campaign.misc.endDate).add(diff).toISOString();
                  } else if (key === "preparationEndDate") {
                    state.campaign.misc.preparationEndDate = value;
                    state.campaign.misc.endDate = dayjs(state.campaign.misc.endDate).add(diff).toISOString();
                  } else if (key === "endDate") {
                    state.campaign.misc.endDate = value;
                  }
                  state.campaign.misc[key] = value;
                },
                false,
                "campaignFormStore/changeCampaignDate",
              );
            },
            changePhaseLength: (key, value) => {
              const campaign = get().campaign.misc;
              const castingStartDate = dayjs(campaign.castingStartDate);
              const castingEndDate = dayjs(campaign.castingEndDate);
              const preparationEndDate = dayjs(campaign.preparationEndDate);
              if (key === "castingPhase") {
                if (campaign.event)
                  get().actions.misc.changeCampaignDate(
                    "castingStartDate",
                    dayjs(campaign.eventStartDate).subtract(value, "days").toISOString(),
                  );
                else
                  get().actions.misc.changeCampaignDate(
                    "castingEndDate",
                    castingStartDate.add(value, "days").toISOString(),
                  );
              } else if (key === "preparationPhase") {
                get().actions.misc.changeCampaignDate(
                  "preparationEndDate",
                  castingEndDate.add(value, "days").toISOString(),
                );
              } else if (key === "activePhase") {
                get().actions.misc.changeCampaignDate("endDate", preparationEndDate.add(value, "days").toISOString());
              }
            },
            initNewCampaign: async ({campaignType}) => {
              const user = useUserStore.getState().user;
              const companyId = getDefaultStore().get(createCampaignForCompanyAtom);
              const selectedCompany = useSelectedCompany();
              let company = selectedCompany;

              if (!!companyId && companyId !== selectedCompany?.id) {
                company = (await client.get(`/company/${companyId}/`)).data;
              }

              set(
                state => {
                  state.campaign.misc.campaignType = campaignType;
                  state.campaign.misc.isRefluencedTagRequired = !(
                    user.package_subscription?.name === "ADVANCED" ||
                    user.package_subscription?.name === "ENTERPRISE" ||
                    user.package_subscription?.name === "PROFESSIONAL" ||
                    user.package_subscription?.name === "EXPERT"
                  );
                  state.campaign.misc.companyId = company?.id;
                  state.campaign.misc.brandName = company?.company_name || "";
                  state.campaign.misc.locations = COUNTRY_FILTERS.includes(company?.country as TCountry)
                    ? [company?.country as TCountry]
                    : ["Switzerland"];
                  state.campaign.misc.isProduct = true;
                  state.campaign.misc.castingStartDate = dayjs().toISOString();
                  state.campaign.misc.castingEndDate = dayjs().add(CASTING_DAYS, "days").toISOString();
                  state.campaign.misc.preparationEndDate = dayjs()
                    .add(CASTING_DAYS + PREPARATION_DAYS, "days")
                    .toISOString();
                  state.campaign.misc.endDate = dayjs()
                    .add(
                      CASTING_DAYS + PREPARATION_DAYS + (campaignType === "ugc" ? ACTIVE_DAYS_UGC : ACTIVE_DAYS),
                      "days",
                    )
                    .toISOString();

                  if (campaignType === "instagram" && company?.username)
                    state.campaign.instagram.accountTags = [company.username];

                  if (campaignType === "tiktok" && company?.tiktok_username)
                    state.campaign.tiktok.accountTags = [company.tiktok_username];

                  if (campaignType !== "ugc") {
                    state.campaign.misc.isProInfluencer = !!user.package_subscription?.pro_creator_mode;
                  }
                },
                false,
                "campaignFormStore/initNewCampaign",
              );
              const response = await get().actions.misc.saveCampaign(true);
              if (campaignType === "ugc") await get().actions.ugc.addUgcVideo(response.data.id);
              set(state => {
                state.campaign.misc.id = response.data.id;
              });
              window.location.href = `/campaign/${response.data.id}/edit/?isUgc=${campaignType === "ugc"}`;

              capturePostHogEvent(PostHogEvents.StartCampaignCreation, {
                campaignId: Number(response.data.id),
                companyId: companyId ? Number(companyId) : undefined,
                campaignType,
                // companyName: company?.company_name, -> we could send this directly to posthog, or use the companyId to fetch it from the company query
              });
            },
            initWithExistingCampaign: campaign => {
              set(
                state => {
                  state.formState.isEditable =
                    campaign.status === "draft" ||
                    campaign.status === "planned" ||
                    useUserStore.getState().user.user_type === "admin";
                  state.campaign = {
                    misc: {
                      brandName: campaign.brand_name || "",
                      isOngoing: Boolean(campaign.on_going),
                      title: campaign.title || "",
                      longDescription: campaign.long_description || "",
                      brandDoes: campaign.brand_does || "",
                      productVariant: campaign.product_variant || "",
                      sellingPrice: campaign.selling_price || 0,
                      isServiceClothing: Boolean(campaign.service_is_clothing),
                      picture: campaign.picture || null,
                      cashReward: campaign.cash_reward || 0,
                      images: campaign.images?.map(image => ({
                        image: image.image,
                        id: image.id,
                      })),
                      status: campaign.status || "draft",
                      campaignType: campaign.ugc ? "ugc" : campaign.tiktok ? "tiktok" : "instagram",
                      id: campaign.id,
                      isRefluencedTagRequired: Boolean(campaign.refluenced_tag_required),
                      castingStartDate: campaign.casting_start || "",
                      castingEndDate: campaign.deadline || "",
                      preparationEndDate: campaign.preparation || "",
                      endDate: campaign.completed || "",
                      locations:
                        campaign.locations?.map((loc: string) =>
                          loc === "Schweiz" ? "Switzerland" : loc === "Deutschland" ? "Germany" : (loc as TCountry),
                        ) ?? [],
                      castingDays: campaign.casting_days,
                      preparationDays: campaign.preparation_days,
                      activeDays: campaign.commercial_days,
                      event: campaign.event,
                      eventAttendedByInfluencers: campaign.event_attended_by_influencer,
                      eventAddressPlaceId: campaign.event_address_place_id,
                      eventAddressDescription: campaign.event_address_description ?? "",
                      eventAddressStreet: campaign.event_address_street,
                      eventAddressPostal: campaign.event_address_postal,
                      eventAddressCity: campaign.event_address_city,
                      eventAddressCountry: campaign.event_address_country,
                      eventStartDate: campaign.event_start_date ?? dayjs().add(21, "days").toISOString(),
                      eventEndDate: campaign.event_end_date
                        ? campaign.event_end_date
                        : campaign.event_start_date
                          ? dayjs(campaign.event_start_date).add(22, "days").toISOString()
                          : dayjs().add(2, "days").toISOString(),
                      eventDuration:
                        campaign.event_start_date &&
                        campaign.event_end_date &&
                        !dayjs(campaign.event_start_date).isSame(campaign.event_end_date, "day")
                          ? "MULTIPLE_DAYS"
                          : "ONE_DAY",
                      isBonusPoolActive: campaign.bonus_pool,
                      companyId: campaign.company_profile?.id,
                      companyImage: campaign.company_profile?.picture320 ?? campaign.company_profile?.picture,
                      canCreateEvents: campaign.company_profile?.event_creator,
                      skipCharacterLimits: campaign.company_profile?.skip_character_limits,
                      hasChosenContentOption: true,
                      isProduct: campaign.service_is_physical_product ?? false,
                      isVoucher: campaign.service_is_voucher ?? false,
                      isCash: campaign.service_is_cash ?? false,
                      isExtended: campaign.is_completed_date_extended ?? false,
                      isProInfluencer: campaign.pro_influencer!,
                      digitalShopUrl: campaign.digital_shop_url,
                    },
                    instagram:
                      !campaign.ugc && !campaign.tiktok
                        ? {
                            isLinkStickerEnabled: campaign.link_sticker !== null,
                            linkSticker: campaign.link_sticker ?? null,
                            isLinkStickerTitleEnabled: !!campaign.link_sticker_title,
                            linkStickerTitle: campaign.link_sticker_title,
                            task: campaign.task || "",
                            requiredContentUploads: {
                              stories: campaign.number_of_stories || 0,
                              posts: campaign.number_of_feedposts || 0,
                              reels: campaign.number_of_reels || 0,
                            },
                            hashtags: (campaign.hashtags ?? "").split(":"),
                            accountTags: (campaign.account_tags ?? "").split(":"),
                            instagramPartnershipAds: !!campaign.instagram_branded_content_ads,
                          }
                        : initialState.campaign.instagram,
                    tiktok: campaign.tiktok
                      ? {
                          task: campaign.task || "",
                          tiktokCampaignCode: campaign.tiktok_campaign_code || "",
                          numberOfMedias: campaign.tiktok_number_of_medias || 0,
                          hashtags: (campaign.hashtags ?? "").split(":"),
                          accountTags: (campaign.account_tags ?? "").split(":"),
                          tiktokSparkAds: campaign.tiktok_spark_ads!,
                        }
                      : initialState.campaign.tiktok,
                    ugc: campaign.ugc ? get().actions.ugc.initWithExistingUgc(campaign) : initialState.campaign.ugc,
                  };
                },
                false,
                "campaignFormStore/initWithExistingCampaign",
              );
              useCampaignManagementStore.getState().setValue("campaign", campaign);

              if (campaign.ugc) get().actions.ugc.calculateInitialCashReward();

              // Update the scheduling for existing draft events
              if (campaign.draft && campaign.event && campaign.preparation_days !== EVENT_PREPARATION_DAYS)
                get().actions.misc.updateScheduling();

              // Re-adjust the dates for those draft campaigns whose casting start date is in the past
              if (campaign.draft && dayjs(campaign.casting_start).isBefore(dayjs(), "day"))
                get().actions.misc.updateScheduling();

              const campaignType = campaign.tiktok ? "tiktok" : campaign.ugc ? "ugc" : "instagram";
              const selectedCompany = useSelectedCompany();
              const instagramAccountTags = get().campaign.instagram.accountTags;
              const tiktokAccountTags = get().campaign.tiktok.accountTags;

              if (
                campaignType === "instagram" &&
                selectedCompany?.username &&
                (!instagramAccountTags.length ||
                  (instagramAccountTags.length === 1 &&
                    ["", selectedCompany.tiktok_username].includes(instagramAccountTags[0])))
              )
                set(state => {
                  state.campaign.instagram.accountTags = [selectedCompany.username!];
                });

              if (
                campaignType === "tiktok" &&
                selectedCompany?.tiktok_username &&
                (!tiktokAccountTags.length ||
                  (tiktokAccountTags.length === 1 && ["", selectedCompany.username].includes(tiktokAccountTags[0])))
              )
                set(state => {
                  state.campaign.tiktok.accountTags = [selectedCompany.tiktok_username!];
                });

              if (
                campaignType === "ugc" ||
                (campaign.cash_reward ?? 0) < BONUS_POOL_MIN_CASH_REWARD ||
                (campaignType === "instagram" && campaign.number_of_feedposts === 0 && campaign.number_of_reels === 0)
              ) {
                set(state => {
                  state.campaign.misc.isBonusPoolActive = false;
                });
              }

              capturePostHogEvent(PostHogEvents.ContinueDraftCampaignCreation, {
                campaignId: Number(campaign.id),
                companyId: campaign.company_profile?.id ? Number(campaign.company_profile?.id) : undefined,
                campaignType,
                // companyName: campaign.company_profile?.company_name,
              });
            },
            saveCampaign: async isDraft => {
              set(
                state => {
                  if (!!get().campaign.instagram.linkSticker) {
                    state.campaign.instagram.linkSticker = urlify(get().campaign.instagram.linkSticker);
                  }
                },
                false,
                "campaignFormStore/urlifyLinkSticker",
              );

              if (!!get().campaign.misc.digitalShopUrl) {
                set(
                  state => {
                    state.campaign.misc.digitalShopUrl = urlify(get().campaign.misc.digitalShopUrl!);
                  },
                  false,
                  "campaignFormStore/urlifyDigitalShopUrl",
                );
              }

              const campaign = get().campaign.misc;

              const payload = {
                ...get().actions.misc.buildPayload(),
                ...get().actions[campaign.campaignType].buildPayload(),
                ...(isDraft !== undefined && {draft: isDraft}),
              };
              if (!campaign.id) {
                const response = await client.post(CAMPAIGN_KEYS.mCampaignList[0], {
                  ...payload,
                  company_profile: get().campaign.misc.companyId,
                });

                if (campaign.campaignType === "ugc") {
                  const implicitlyGeneratedUgcVideoId = response.data.ugc_details[0].id as number;
                  await Promise.all([
                    get().actions.ugc.saveUgcChanges(),
                    client.post(CAMPAIGN_KEYS.mCampaignUgcContentRemoval[0], {
                      ugc_content_id: implicitlyGeneratedUgcVideoId,
                    }),
                  ]);
                }

                return response;
              } else {
                if (campaign.campaignType === "ugc") {
                  await get().actions.ugc.saveUgcChanges();
                }

                const response = await client.patch(CAMPAIGN_KEYS.mCampaign(campaign.id!)[0], payload);
                [
                  CAMPAIGN_KEYS.qCampaignInvitedGroups(campaign.id),
                  GROUP_KEYS.qGroupList(useGroupListQueryParams({companyId: campaign.companyId})),
                ].map(queryKey => queryClient.refetchQueries({queryKey}));

                return response;
              }
            },
            resetStore: () => {
              set(
                state => {
                  state.campaign = initialState.campaign;
                  state.formState = initialState.formState;
                },
                false,
                "campaignFormStore/resetStore",
              );
            },
            validate: () => {
              set(
                state => {
                  state.formState.errors = [];
                },
                false,
                "campaignFormStore/clearErrors",
              );

              // Determine the base schema first
              let baseSchema = get().campaign.misc.skipCharacterLimits
                ? CampaignMiscSchema.omit({
                    longDescription: true,
                    brandDoes: true,
                  })
                : CampaignMiscSchema;

              // Apply extensions conditionally
              if (get().campaign.misc.isVoucher) {
                baseSchema = baseSchema.extend({
                  digitalShopUrl: z.preprocess(value => value ?? "", z.string().min(1, "digital_shop_url_required")),
                });
              }

              if (get().campaign.misc.event) {
                baseSchema = baseSchema.extend({
                  eventAddressDescription: z.string().min(1, "event_location_required"),
                });
              }

              // Determine the final schema, potentially omitting castingDays
              const finalSchema =
                get().campaign.misc.isOngoing || get().campaign.misc.event
                  ? baseSchema.omit({castingDays: true})
                  : baseSchema;

              const isAdmin = useUserStore.getState().user.user_type === "admin";
              const campaignMisc = get().campaign.misc;

              // Parse with the final schema
              const result = finalSchema.safeParse(campaignMisc);

              if (!result.success && !isAdmin) {
                set(
                  state => {
                    state.formState.errors = state.formState.errors.concat(
                      result.error.issues.map(i => {
                        if (get().campaign.misc.isVoucher && i.message.startsWith("product_description")) {
                          return i.message.replace("product_description", "voucher_description");
                        }

                        if (get().campaign.misc.isCash && i.message.startsWith("product_description")) {
                          return i.message.replace("product_description", "cash_description");
                        }

                        return i.message;
                      }),
                    );
                  },
                  false,
                  "campaignFormStore/misc/validate",
                );
              }

              const isPriceValid = get().campaign.misc.isCash
                ? get().campaign.misc.campaignType === "ugc"
                  ? (get().campaign.ugc.extraCashReward ?? 0) > 0
                  : (get().campaign.misc.cashReward ?? 0) > 0
                : (get().campaign.misc.sellingPrice ?? 0) > 0 ||
                  (get().campaign.misc.campaignType === "ugc"
                    ? (get().campaign.ugc.extraCashReward ?? 0) > 0
                    : (get().campaign.misc.cashReward ?? 0) > 0);

              if (!isPriceValid)
                set(
                  state => {
                    state.formState.errors.push(
                      get().campaign.misc.isCash ? "cash_reward_min_error" : "price_min_error",
                    );
                  },
                  false,
                  "campaignFormStore/misc/validate",
                );

              return isAdmin || (get().actions[campaignMisc.campaignType].validate() && result.success && isPriceValid);
            },
            updateScheduling: () => {
              const campaign = get().campaign.misc;
              const setCampaignValue = get().actions.misc.setMiscValue;
              const castingDays = campaign.castingDays ?? CASTING_DAYS;
              const preparationDays = campaign.preparationDays ?? PREPARATION_DAYS;
              const activeDays =
                campaign.activeDays ?? (campaign.campaignType === "ugc" ? ACTIVE_DAYS_UGC : ACTIVE_DAYS);

              setCampaignValue(
                "castingStartDate",
                campaign.event ? dayjs(campaign.castingStartDate).toISOString() : dayjs().toISOString(),
              );
              setCampaignValue(
                "castingEndDate",
                campaign.event
                  ? dayjs(campaign.eventStartDate!).subtract(EVENT_PREPARATION_DAYS, "days").toISOString()
                  : dayjs().add(castingDays, "days").toISOString(),
              );
              setCampaignValue(
                "preparationEndDate",
                campaign.event
                  ? campaign.eventStartDate!
                  : dayjs()
                      .add(castingDays + preparationDays, "days")
                      .toISOString(),
              );
              setCampaignValue(
                "endDate",
                campaign.event
                  ? dayjs(campaign.eventEndDate).add(EVENT_ACTIVE_DAYS, "days").toISOString()
                  : dayjs()
                      .add(castingDays + preparationDays + activeDays, "days")
                      .toISOString(),
              );
            },
            resetEventAddress: () => {
              const setCampaignValue = get().actions.misc.setMiscValue;
              setCampaignValue("eventAddressPlaceId", null);
              setCampaignValue("eventAddressStreet", null);
              setCampaignValue("eventAddressPostal", null);
              setCampaignValue("eventAddressCity", null);
              setCampaignValue("eventAddressCountry", null);
              setCampaignValue("eventAddressDescription", "");
            },
          },
          instagram: {
            buildPayload: () => {
              const campaign = get().campaign.instagram;
              return {
                number_of_feedposts:
                  campaign.requiredContentUploads && "posts" in campaign.requiredContentUploads
                    ? campaign.requiredContentUploads.posts
                    : 0,
                number_of_stories: campaign.requiredContentUploads?.stories ?? 0,
                number_of_reels:
                  campaign.requiredContentUploads && "reels" in campaign.requiredContentUploads
                    ? campaign.requiredContentUploads.reels
                    : 0,
                hashtags: campaign.hashtags.map(tag => tag.replace("#", "")).join(":"),
                account_tags: campaign.accountTags.map(tag => tag.replace("@", "")).join(":"),
                task: campaign.task,
                link_sticker: campaign.linkSticker,
                link_sticker_title: campaign.linkStickerTitle,
                instagram_branded_content_ads: campaign.instagramPartnershipAds,
              };
            },
            setInstagramValue: (key, value) =>
              set(
                state => {
                  if (
                    key === "requiredContentUploads" &&
                    (get().campaign.misc.cashReward ?? 0) >= BONUS_POOL_MIN_CASH_REWARD
                  ) {
                    const isOldCampaignStoryOnly =
                      get().campaign.instagram.requiredContentUploads?.posts! === 0 &&
                      get().campaign.instagram.requiredContentUploads?.reels! === 0;
                    const isNewCampaignStoryOnly =
                      (value as TContentRequirements)?.posts! === 0 && (value as TContentRequirements)?.reels! === 0;

                    if (isOldCampaignStoryOnly && !isNewCampaignStoryOnly) {
                      state.campaign.misc.isBonusPoolActive = true;
                    }

                    if (!isOldCampaignStoryOnly && isNewCampaignStoryOnly) {
                      state.campaign.misc.isBonusPoolActive = false;
                    }
                  }

                  state.campaign.instagram[key] = value;
                },
                false,
                "campaignFormStore/setInstagramValue",
              ),
            changeHashTag: newValue =>
              set(
                state => {
                  const updatedTags: string[] | undefined = getChangedTags("#", newValue);

                  if (updatedTags) {
                    state.campaign.instagram.hashtags = updatedTags;
                  }
                },
                false,
                "campaignFormStore/changeHashTag",
              ),
            changeAccountTag: newValue =>
              set(
                state => {
                  const updatedTags: string[] | undefined = getChangedTags("@", newValue);

                  if (updatedTags) {
                    state.campaign.instagram.accountTags = updatedTags;
                  }
                },
                false,
                "campaignFormStore/changeAccountTag",
              ),
            validate: () => {
              const isAdmin = useUserStore.getState().user.user_type === "admin";
              const schema = get().campaign.misc.skipCharacterLimits
                ? CampaignInstagramSchema.omit({
                    task: true,
                  }).extend({
                    task: z.string().min(1, "task_required"),
                  })
                : CampaignInstagramSchema;
              const result = schema.safeParse(get().campaign.instagram);
              if (!result.success && !isAdmin) {
                set(
                  state => {
                    state.formState.errors = state.formState.errors.concat(result.error.issues.map(i => i.message));
                  },
                  false,
                  "campaignFormStore/instagram/validate",
                );
              }

              const requiredContentUploads = get().campaign.instagram.requiredContentUploads;

              if (
                !requiredContentUploads ||
                (requiredContentUploads.stories === 0 &&
                  requiredContentUploads.reels === 0 &&
                  requiredContentUploads.posts === 0)
              ) {
                set(
                  state => {
                    state.formState.errors.push("content_option_required");
                  },
                  false,
                  "campaignFormStore/instagram/validate",
                );
              }

              if (get().campaign.instagram.isLinkStickerEnabled && !get().campaign.instagram.linkSticker) {
                set(
                  state => {
                    state.formState.errors.push("linksticker_required");
                  },
                  false,
                  "campaignFormStore/instagram/validate",
                );
              }

              return (
                isAdmin ||
                (result.success &&
                  !get().formState.errors.length &&
                  !!requiredContentUploads &&
                  !(
                    requiredContentUploads.stories === 0 &&
                    requiredContentUploads.reels === 0 &&
                    requiredContentUploads.posts === 0
                  ))
              );
            },
          },
          tiktok: {
            buildPayload: () => {
              const campaign = get().campaign.tiktok;
              return {
                tiktok: true,
                task: campaign.task,
                hashtags: campaign.hashtags.map(tag => tag?.replace("#", "")).join(":"),
                account_tags: campaign.accountTags.map(tag => tag?.replace("@", "")).join(":"),
                tiktok_campaign_code: campaign.tiktokCampaignCode,
                tiktok_number_of_medias: campaign.numberOfMedias,
                tiktok_spark_ads: campaign.tiktokSparkAds,
              };
            },
            setTiktokValue: (key, value) =>
              set(
                state => {
                  state.campaign.tiktok[key] = value;
                },
                false,
                "campaignFormStore/setTiktokValue",
              ),
            changeHashTag: newValue =>
              set(
                state => {
                  const updatedTags: string[] | undefined = getChangedTags("#", newValue);

                  if (updatedTags) {
                    state.campaign.tiktok.hashtags = updatedTags;
                  }
                },
                false,
                "campaignFormStore/changeHashTag",
              ),
            changeAccountTag: newValue =>
              set(
                state => {
                  const updatedTags: string[] | undefined = getChangedTags("@", newValue);

                  if (updatedTags) {
                    state.campaign.tiktok.accountTags = updatedTags;
                  }
                },
                false,
                "campaignFormStore/changeAccountTag",
              ),
            validate: () => {
              const isAdmin = useUserStore.getState().user.user_type === "admin";
              const schema = get().campaign.misc.skipCharacterLimits
                ? CampaignTiktokSchema.omit({
                    task: true,
                  }).extend({
                    task: z.string().min(1, "task_required"),
                  })
                : CampaignTiktokSchema;
              const result = schema.safeParse(get().campaign.tiktok);
              if (!result.success && !isAdmin) {
                set(
                  state => {
                    state.formState.errors = state.formState.errors.concat(result.error.issues.map(i => i.message));
                  },
                  false,
                  "campaignFormStore/tiktok/validate",
                );
              }
              return isAdmin || result.success;
            },
          },
          ugc: {
            buildPayload: () => {
              const campaign = get().campaign.ugc;
              return {
                ugc: true,
                ugc_dont_description: campaign.ugcDontsDescription,
                subtitles: campaign.isWithSubtitles,
                source_material: campaign.isSourceMaterialRequested,
                number_of_ugc: campaign.updatedUgcContents.length,
                content_language: campaign.contentLanguage,
                ugc_extra_cash_reward: campaign.extraCashReward ?? 0,
              };
            },
            setUgcValue: (key, value) =>
              set(
                state => {
                  state.campaign.ugc[key] = value;
                },
                false,
                "campaignFormStore/setUgcValue",
              ),
            initWithExistingUgc: campaign => {
              const isWithSubtitles = campaign.ugc_details?.length
                ? !campaign.ugc_details[0]?.scenes
                    ?.find((scene: {name: string}) => scene.name === "Untertitel")
                    ?.content.includes("nicht erwünscht") || false
                : false;

              const isSourceContentNeeded = campaign.ugc_details?.length
                ? !campaign.ugc_details[0]?.scenes
                    ?.find(scene => scene.name === "Rohmaterial")
                    ?.content.includes("nicht erwünscht") || false
                : false;

              const ugcContents = (campaign.ugc_details ?? []).map((content: TUgcCampaign["ugcContents"][number]) => ({
                id: content.id,
                length: content.length,
                name: content.name,
                scenes: (content.scenes as TUgcCampaign["ugcContents"][number]["scenes"])
                  .filter(
                    (scene: {name: string}) =>
                      scene.name !== "Untertitel" && scene.name !== "Rohmaterial" && scene.name !== "Sprache",
                  )
                  .map(scene => ({
                    content: scene.content,
                    name: scene.name,
                    place_holder: scene.place_holder,
                  })),
              }));

              return {
                ugcDontsDescription: campaign.ugc_dont_description || "",
                isWithSubtitles,
                isSourceMaterialRequested: isSourceContentNeeded,
                contentLanguage: campaign.content_language,
                ugcContents: ugcContents,
                updatedUgcContents: ugcContents.slice(),
                extraCashReward: campaign.ugc_extra_cash_reward,
                initialCashReward: null,
              };
            },
            saveUgcChanges: async () => {
              const ugcValues = get().campaign.ugc;
              if (isEqual(ugcValues.updatedUgcContents, ugcValues.ugcContents)) return;
              const updatedVideos = ugcValues.updatedUgcContents.filter(ugcContent =>
                ugcValues.ugcContents.find(
                  updatedContent => updatedContent.id === ugcContent.id && !isEqual(updatedContent, ugcContent),
                ),
              );
              await Promise.all([
                ...updatedVideos.map((ugcContent, idx) =>
                  client.post(CAMPAIGN_KEYS.mCampaignUgcDetailsUpdate[0], {
                    length: ugcContent.length,
                    name: "Video " + (idx + 1),
                    scenes: ugcContent.scenes,
                    ugc_content_id: ugcContent.id,
                  }),
                ),
              ]);
            },
            addUgcVideo: async campaignId => {
              const response = await client.post(
                CAMPAIGN_KEYS.mCampaignUgcContent(campaignId || get().campaign.misc.id!)[0],
              );

              set(
                state => {
                  state.campaign.ugc.updatedUgcContents.push(response.data);
                  state.campaign.ugc.ugcContents.push(response.data);
                },
                false,
                "campaignFormStore/addUgcVideo",
              );
              get().actions.ugc.calculateInitialCashReward();
            },
            deleteUgcVideo: async index => {
              const ugcContent = get().campaign.ugc.updatedUgcContents[index];
              if (ugcContent.id) {
                await client.post(CAMPAIGN_KEYS.mCampaignUgcContentRemoval[0], {ugc_content_id: ugcContent.id});
              }
              set(
                state => {
                  state.campaign.ugc.updatedUgcContents.splice(index, 1);
                },
                false,
                "campaignFormStore/deleteUgcVideo",
              );
              get().actions.ugc.calculateInitialCashReward();
            },
            addUgcScene: index =>
              set(
                state => {
                  state.campaign.ugc.updatedUgcContents[index].scenes.push({
                    content: "",
                    name: "",
                    place_holder: "",
                  });
                },
                false,
                "campaignFormStore/addUgcScene",
              ),
            deleteUgcScene: (videoIndex, sceneIndex) =>
              set(
                state => {
                  state.campaign.ugc.updatedUgcContents[videoIndex].scenes.splice(sceneIndex, 1);
                },
                false,
                "campaignFormStore/deleteUgcScene",
              ),
            changeUgcScene: (videoIndex, sceneIndex, key, value) =>
              set(
                state => {
                  state.campaign.ugc.updatedUgcContents[videoIndex].scenes[sceneIndex][key] = value;
                },
                false,
                "campaignFormStore/changeUgcScene",
              ),
            changeUgcVideo: (index, key, value) => {
              set(
                state => {
                  state.campaign.ugc.updatedUgcContents[index][key] = value;
                },
                false,
                "campaignFormStore/changeUgcVideo",
              );
              get().actions.ugc.calculateInitialCashReward();
            },
            calculateInitialCashReward: () => {
              const reward = sumBy(get().campaign.ugc.updatedUgcContents, content => {
                if (content.length === 30) return UGC_COST_PER_VIDEO_30_SECONDS * UGC_CASH_REWARD_PERCENTAGE;
                else if (content.length === 15) return UGC_COST_PER_VIDEO_15_SECONDS * UGC_CASH_REWARD_PERCENTAGE;
                else return 0;
              });

              set(state => {
                state.campaign.ugc.initialCashReward = Math.floor(reward);
              });
            },
            validate: () => {
              const isAdmin = useUserStore.getState().user.user_type === "admin";
              const schema = get().campaign.misc.skipCharacterLimits
                ? CampaignUgcSchema.omit({
                    ugcDontsDescription: true,
                  })
                : CampaignUgcSchema;
              const result = schema.safeParse(get().campaign.ugc);
              if (!result.success && !isAdmin) {
                set(
                  state => {
                    state.formState.errors = state.formState.errors.concat(result.error.issues.map(i => i.message));
                  },
                  false,
                  "campaignFormStore/ugc/validate",
                );
              }
              return isAdmin || result.success;
            },
          },
        },
      }),
      {
        name: "Campaign Form Store",
      },
    ),
  ),
);

export type TInstagramCampaign = TComposedCampaign["misc"] & TComposedCampaign["instagram"];

export type TTiktokCampaign = TComposedCampaign["misc"] & TComposedCampaign["tiktok"];
export type TUgcCampaign = TComposedCampaign["misc"] & TComposedCampaign["ugc"];

export function useComposedInstagramCampaignState(): TInstagramCampaign;
export function useComposedInstagramCampaignState<TReturnType>(
  selector: (state: TInstagramCampaign) => TReturnType,
): TReturnType;

export function useComposedInstagramCampaignState<TReturnType>(selector?: (state: TInstagramCampaign) => TReturnType) {
  return useCampaignFormStore(
    useShallow(state =>
      selector
        ? selector({...state.campaign.misc, ...state.campaign.instagram})
        : ({...state.campaign.misc, ...state.campaign.instagram} as TReturnType),
    ),
  );
}

export function useComposedTiktokCampaignState(): TTiktokCampaign;
export function useComposedTiktokCampaignState<TReturnType>(
  selector: (state: TTiktokCampaign) => TReturnType,
): TReturnType;

export function useComposedTiktokCampaignState<TReturnType>(selector?: (state: TTiktokCampaign) => TReturnType) {
  return useCampaignFormStore(
    useShallow(state =>
      selector
        ? selector({...state.campaign.misc, ...state.campaign.tiktok})
        : ({...state.campaign.misc, ...state.campaign.tiktok} as TReturnType),
    ),
  );
}

export function useComposedUgcCampaignState(): TUgcCampaign;
export function useComposedUgcCampaignState<TReturnType>(selector: (state: TUgcCampaign) => TReturnType): TReturnType;

export function useComposedUgcCampaignState<TReturnType>(selector?: (state: TUgcCampaign) => TReturnType) {
  return useCampaignFormStore(
    useShallow(state =>
      selector
        ? selector({...state.campaign.misc, ...state.campaign.ugc})
        : ({...state.campaign.misc, ...state.campaign.ugc} as TReturnType),
    ),
  );
}

export function useComposedCampaignState(): TComposedCampaign;
export function useComposedCampaignState<TReturnType>(selector: (state: TComposedCampaign) => TReturnType): TReturnType;

export function useComposedCampaignState<TReturnType>(selector?: (state: TComposedCampaign) => TReturnType) {
  return useCampaignFormStore(
    useShallow(state => (selector ? selector(state.campaign) : (state.campaign as TReturnType))),
  );
}

export const useSetMiscValue = () => useCampaignFormStore(state => state.actions.misc.setMiscValue);

export const useSetInstagramValue = () => useCampaignFormStore(state => state.actions.instagram.setInstagramValue);

export const useSetTiktokValue = () => useCampaignFormStore(state => state.actions.tiktok.setTiktokValue);

export const useSetUgcValue = () => useCampaignFormStore(state => state.actions.ugc.setUgcValue);

export const useSetCampaignFormState = () => useCampaignFormStore(state => state.actions.misc.setFormState);
export const useUpdateScheduling = () => useCampaignFormStore(state => state.actions.misc.updateScheduling);

export function useCampaignFormState(): TFormState;
export function useCampaignFormState<TReturnType>(selector: (state: TFormState) => TReturnType): TReturnType;

export function useCampaignFormState<TReturnType>(selector?: (state: TFormState) => TReturnType) {
  return useCampaignFormStore(state => (selector ? selector(state.formState) : (state.formState as TReturnType)));
}

export function useCampaignFormActions(): TActions;
export function useCampaignFormActions<TReturnType>(selector: (state: TActions) => TReturnType): TReturnType;

export function useCampaignFormActions<TReturnType>(selector?: (state: TActions) => TReturnType) {
  return useCampaignFormStore(state => (selector ? selector(state.actions) : (state.actions as TReturnType)));
}

export const useInstagramActions = () => useCampaignFormActions().instagram;
export const useTiktokActions = () => useCampaignFormActions().tiktok;
export const useUgcActions = () => useCampaignFormActions().ugc;
export const useMiscActions = () => useCampaignFormActions().misc;
